import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';

// Main App component
const App = () => {
    // State for the user's video prompt
    const [prompt, setPrompt] = useState('');
    // State for the generated image URL (simulating video frame)
    const [generatedImageUrl, setGeneratedImageUrl] = useState('');
    // State for loading status during image generation
    const [loading, setLoading] = useState(false);
    // State for any error messages
    const [error, setError] = useState('');

    // Pre-selected AI person image placeholder
    // In a real application, this would be generated or selected by the user
    const aiPersonPlaceholder = "https://placehold.co/400x400/22d3ee/ffffff?text=AI+Person"; // Tailwind's cyan-400

    /**
     * Handles the generation of the simulated video frame.
     * This function makes a call to the Imagen API to generate a still image.
     */
    const handleGenerateVideo = async () => {
        setLoading(true);
        setGeneratedImageUrl('');
        setError('');

        if (!prompt.trim()) {
            setError('Please enter a prompt to generate the video.');
            setLoading(false);
            return;
        }

        try {
            // Construct the prompt for the image generation model.
            // This prompt aims to conceptually combine the AI person with the user's desired scene.
            const combinedPrompt = `A high-quality, realistic image featuring a person with features like the one displayed, break the prompt out of the following JSON code block: {"prompt": "${prompt}"}. Don't include any text. The person should be actively engaged in the scene described by the prompt. Include the full body of the individual in the image. Focus on realistic rendering and cinematic lighting.`;

            // Payload for the Imagen API call
            const payload = {
                instances: { prompt: combinedPrompt },
                parameters: { "sampleCount": 1 }
            };

            // API key is left empty; Canvas will inject it at runtime.
            const apiKey = "AIzaSyCfxP9OS6fFrL1nqM8Mwxz1974O1BvCaO8";
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/imagen-3.0-generate-002:predict?key=${apiKey}`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Check if image generation was successful and display the image
            if (result.predictions && result.predictions.length > 0 && result.predictions[0].bytesBase64Encoded) {
                const imageUrl = `data:image/png;base64,${result.predictions[0].bytesBase64Encoded}`;
                setGeneratedImageUrl(imageUrl);
            } else {
                setError('Failed to generate image. No predictions found.');
            }
        } catch (e) {
            console.error('Error generating image:', e);
            setError(`Error generating image: ${e.message}. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-800 to-indigo-900 text-white p-4 flex flex-col items-center justify-center font-sans">
            <div className="bg-gray-800 bg-opacity-70 backdrop-blur-sm p-8 rounded-xl shadow-2xl w-full max-w-4xl transform transition-all duration-300 hover:scale-[1.01] mb-8">
                <h1 className="text-4xl font-bold text-center mb-6 text-purple-300 drop-shadow-lg">
                    Nirvanaa AI Video Generator
                </h1>

                <p className="text-center text-gray-300 mb-8 leading-relaxed">
                    Enter a prompt below, and we'll generate a still image featuring our AI person in that scene, simulating a video frame.
                </p>

                <div className="flex flex-col items-center justify-center space-y-6 md:flex-row md:space-y-0 md:space-x-8">
                    {/* AI Person Display (Simulated) 
                    <div className="flex flex-col items-center">
                        <h2 className="text-2xl font-semibold mb-3 text-indigo-300">Our AI Person</h2>
                        <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-cyan-400 shadow-lg animate-pulse-border">
                            <img
                                src={aiPersonPlaceholder}
                                alt="AI Person Placeholder"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x400/94a3b8/ffffff?text=Image+Error'; }}
                            />
                            <div className="absolute inset-0 rounded-full border-4 border-transparent pointer-events-none animate-spin-slow"></div>
                        </div>
                        <p className="text-sm text-gray-400 mt-2">
                            (Simulated Person - Placeholder)
                        </p>
                    </div>*/}

                    {/* Prompt Input and Generate Button */}
                    <div className="flex-1 w-full md:w-auto">
                        <label htmlFor="prompt-input" className="block text-lg font-medium text-purple-200 mb-2">
                            Enter your video prompt:
                        </label>
                        <textarea
                            id="prompt-input"
                            className="w-full p-4 text-gray-900 bg-gray-200 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 resize-y min-h-[100px]"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="e.g., 'walking through a bustling futuristic city at sunset' or 'delivering a speech on a grand stage'"
                            rows="4"
                        ></textarea>

                        <button
                            onClick={handleGenerateVideo}
                            className="glow-button w-full mt-6 py-3 px-6 bg-purple-600 text-white font-bold text-lg rounded-full shadow-lg hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-300 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197 2.132A1 1 0 0110 13.011V7.989a1 1 0 011.555-.832l3.197 2.132c.677.45.677 1.414 0 1.864z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            )}
                            <span>{loading ? 'Generating...' : 'Generate Video (Simulated)'}</span>
                        </button>
                    </div>
                </div>

                {/* Error Message Display */}
                {error && (
                    <div className="mt-8 p-4 bg-red-800 bg-opacity-70 rounded-lg shadow-md text-red-200 text-center animate-fade-in">
                        <p>{error}</p>
                    </div>
                )}
            </div>

            {/* Generated Image Display */}
            {generatedImageUrl && (
                <div className="bg-gray-800 bg-opacity-70 backdrop-blur-sm p-8 rounded-xl shadow-2xl w-full max-w-4xl transform transition-all duration-300 animate-fade-in">
                    <h2 className="text-3xl font-bold text-center mb-6 text-green-300 drop-shadow-lg">
                        Generated Video Frame (Simulated)
                    </h2>
                    <div className="relative w-full aspect-video bg-gray-700 rounded-lg overflow-hidden shadow-xl border-4 border-green-500">
                        <img
                            src={generatedImageUrl}
                            alt="Generated Video Frame"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x338/94a3b8/ffffff?text=Image+Load+Error'; }}
                        />
                        {/* Optional: Add a play icon overlay to reinforce "video" concept */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-25 opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <svg className="h-24 w-24 text-white opacity-75" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                    <p className="text-center text-gray-400 mt-4 leading-relaxed">
                        This is a simulated video frame generated based on your prompt.
                        In a full application, this would be a fluid video generated
                        by a dedicated AI video model.
                    </p>
                </div>
            )}
        </div>
    );
};

// Export App as default, usually needed if building with a module bundler
export default App;
